import type { MetaStructureResponse } from '@nono/contract';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { cn } from '@/lib/utils';
import {
  BLUEPRINT,
  BLUEPRINT_AS_OF,
  CHANNELS,
  DEFAULT_DAILY_NOK,
  ENTRY_BY_ID,
  ENTRY_POINTS,
  STRUCTURE_RULES,
  TEST_LANE,
} from './blueprint';
import { MetaShell } from './MetaShell';
import { flatAds, int, isLive, kr, resultKind, unitCosts, useMetaStructure } from './model';

/*
 * Meta → Systemet. The structure the account should have, drawn as one flow:
 * door → campaign → where the click lands → what Meta learns. Below it the
 * account as it is today next to the plan, and a budget lever that turns the
 * plan into expected bookings using the account's own history.
 */

/** Show-up and join rates for bookings made in Nono's own widget (PR quick-facts). */
const SHOW_UP = 0.9;
const JOIN = 0.77;
/** Form lead → booked within 7 days, Aug–Sep (PR ads-momence-match §6). */
const FORM_BOOK_RATE = 0.11;

export function MetaSystemPage() {
  const q = useMetaStructure();
  const [budget, setBudget] = useState(DEFAULT_DAILY_NOK);
  const d = q.data?.data;

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !d) return <ErrorState onRetry={() => void q.refetch()} />;

  const econ = unitCosts(d, 'lifetime');

  return (
    <MetaShell
      title="Systemet"
      lead={
        <>
          Tre kampanjer, ett annonsesett i hver, og en testbane ved siden av. Hver annonse snakker
          gjennom én inngangsdør. Alt måles på det som betyr noe: en booket prøvetime. Tegnet{' '}
          {BLUEPRINT_AS_OF}. Et forslag til vi har kjørt det, ikke en fasit.
        </>
      }
    >
      <Card className="overflow-x-auto p-2 sm:p-3">
        <FlowDiagram />
      </Card>

      <div className="grid gap-3 lg:grid-cols-3">
        {BLUEPRINT.map((b) => (
          <Card key={b.id} className="flex flex-col gap-2 p-3 sm:p-4">
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="text-section text-fg">{b.name}</h2>
              <span className="tabular text-label font-semibold text-fg">{Math.round(b.budgetShare * 100)} %</span>
            </div>
            <p className="text-body text-fg">{b.role}</p>
            <p className="text-label text-fg-muted">{b.setup}</p>
            <div className="flex flex-wrap gap-1.5">
              <Badge>{b.adsets} annonsesett</Badge>
              <Badge>{b.ads} annonser</Badge>
              {b.entries.map((e) => (
                <span
                  key={e}
                  className="inline-flex items-center gap-1 rounded-full bg-surface-3 px-2 py-0.5 text-micro text-fg-muted"
                >
                  <span className="size-1.5 rounded-full" style={{ background: ENTRY_BY_ID.get(e)?.color }} />
                  {ENTRY_BY_ID.get(e)?.name}
                </span>
              ))}
            </div>
            <p className="text-label text-fg-muted">
              <span className="font-medium text-fg">Hvorfor: </span>
              {b.why}
            </p>
            {b.precondition && (
              <p className="rounded-md bg-warn-tint px-2.5 py-1.5 text-label text-warn-fg">
                Før den slås på: {b.precondition}
              </p>
            )}
          </Card>
        ))}
      </div>

      <Card className="p-3 sm:p-4">
        <h2 className="text-section text-fg">{TEST_LANE.name}</h2>
        <p className="mt-1 text-body text-fg-muted">{TEST_LANE.rule}</p>
      </Card>

      <div className="grid gap-3 lg:grid-cols-2">
        <NowVsPlan d={d} />
        <BudgetLever budget={budget} setBudget={setBudget} econ={econ} />
      </div>

      <Card className="p-3 sm:p-4">
        <h2 className="mb-2 text-section text-fg">Reglene strukturen følger</h2>
        <ol className="grid gap-3 sm:grid-cols-2">
          {STRUCTURE_RULES.map((r, i) => (
            <li key={r.rule} className="flex gap-3">
              <span className="tabular flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-3 text-label font-semibold text-fg">
                {i + 1}
              </span>
              <div>
                <div className="text-body font-medium text-fg">{r.rule}</div>
                <div className="text-label text-fg-muted">{r.why}</div>
              </div>
            </li>
          ))}
        </ol>
      </Card>

      <Card className="p-3 sm:p-4">
        <h2 className="mb-2 text-section text-fg">Kanaler</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          {CHANNELS.map((c) => (
            <div key={c.name} className="rounded-md border border-border p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-body font-medium text-fg">{c.name}</span>
                <Badge tone={c.verdict === 'nå' ? 'good' : c.verdict === 'fase 2' ? 'brand' : 'neutral'}>
                  {c.verdict}
                </Badge>
              </div>
              <p className="mt-1 text-label text-fg-muted">{c.why}</p>
            </div>
          ))}
        </div>
      </Card>
    </MetaShell>
  );
}

/* ── The flow: door → campaign → landing → what Meta learns ──────────────── */

const FW = 1080;
const FH = 430;
const COLS = [30, 300, 610, 850];

function FlowDiagram() {
  const doorY = (i: number) => 70 + i * 70;
  const campY: Record<string, number> = { motor: 110, skjema: 245, varm: 345 };
  const landY = { site: 150, form: 280 };
  const learnY = { schedule: 150, crm: 280 };

  const curve = (x1: number, y1: number, x2: number, y2: number) =>
    `M ${x1} ${y1} C ${(x1 + x2) / 2} ${y1}, ${(x1 + x2) / 2} ${y2}, ${x2} ${y2}`;

  const head = (x: number, label: string) => (
    <text x={x} y={26} className="fill-[var(--fg-subtle)] font-semibold uppercase tracking-wider" fontSize={11}>
      {label}
    </text>
  );

  return (
    <svg viewBox={`0 0 ${FW} ${FH}`} className="block h-auto w-full min-w-[860px]" role="img" aria-label="Flyten i Meta-systemet">
      {head(COLS[0]!, 'Inngangsdør')}
      {head(COLS[1]!, 'Kampanje')}
      {head(COLS[2]!, 'Hvor de lander')}
      {head(COLS[3]!, 'Hva Meta lærer')}

      {/* door → campaign threads */}
      {BLUEPRINT.flatMap((b) =>
        b.entries.map((e) => {
          const i = ENTRY_POINTS.findIndex((x) => x.id === e);
          return (
            <path
              key={`${b.id}-${e}`}
              d={curve(COLS[0]! + 210, doorY(i), COLS[1]!, campY[b.id]!)}
              fill="none"
              stroke={ENTRY_BY_ID.get(e)?.color}
              strokeOpacity={0.55}
              strokeWidth={1.6}
            />
          );
        }),
      )}
      {/* campaign → landing */}
      <path d={curve(COLS[1]! + 230, campY.motor!, COLS[2]!, landY.site)} stroke="var(--accent-booking)" strokeWidth={2.5} fill="none" />
      <path d={curve(COLS[1]! + 230, campY.varm!, COLS[2]!, landY.site)} stroke="var(--accent-booking)" strokeWidth={1.6} strokeOpacity={0.6} fill="none" />
      <path d={curve(COLS[1]! + 230, campY.skjema!, COLS[2]!, landY.form)} stroke="var(--accent-lead)" strokeWidth={1.8} fill="none" />
      {/* landing → learning */}
      <path d={curve(COLS[2]! + 200, landY.site, COLS[3]!, learnY.schedule)} stroke="var(--accent-booking)" strokeWidth={2.5} fill="none" />
      <path d={curve(COLS[2]! + 200, landY.form, COLS[3]!, learnY.crm)} stroke="var(--accent-lead)" strokeWidth={1.8} fill="none" />
      {/* the call turns a form lead into a booking */}
      <path d={curve(COLS[2]! + 100, landY.form - 30, COLS[2]! + 100, landY.site + 32)} stroke="var(--warn)" strokeDasharray="4 4" strokeWidth={1.4} fill="none" />
      <text x={COLS[2]! + 108} y={(landY.form + landY.site) / 2 + 4} className="fill-[var(--warn-fg)]" fontSize={11}>
        samtale samme dag
      </text>

      {ENTRY_POINTS.map((e, i) => (
        <g key={e.id}>
          <rect x={COLS[0]} y={doorY(i) - 22} width={210} height={44} rx={10} fill="var(--surface-2)" stroke="var(--border)" />
          <circle cx={COLS[0]! + 18} cy={doorY(i)} r={5} fill={e.color} />
          <text x={COLS[0]! + 32} y={doorY(i) - 3} className="fill-[var(--fg)] font-medium" fontSize={12.5}>
            {e.name.length > 25 ? `${e.name.slice(0, 24)}…` : e.name}
          </text>
          <text x={COLS[0]! + 32} y={doorY(i) + 13} className="fill-[var(--fg-subtle)]" fontSize={10.5}>
            {e.proof === 'bevist' ? 'Bevist i tallene' : e.proof === 'lovende' ? 'Lovende' : 'Nesten ikke testet'}
          </text>
        </g>
      ))}

      {BLUEPRINT.map((b) => {
        const y = campY[b.id]!;
        const h = b.id === 'motor' ? 92 : 64;
        return (
          <g key={b.id}>
            <rect
              x={COLS[1]}
              y={y - h / 2}
              width={230}
              height={h}
              rx={12}
              fill={b.id === 'motor' ? 'var(--brand-tint)' : 'var(--surface-2)'}
              stroke={b.id === 'motor' ? 'var(--brand)' : 'var(--border-strong)'}
              strokeWidth={b.id === 'motor' ? 1.6 : 1}
            />
            <text x={COLS[1]! + 14} y={y - h / 2 + 24} className="fill-[var(--fg)] font-semibold" fontSize={14}>
              {b.name}
            </text>
            <text x={COLS[1]! + 14} y={y - h / 2 + 42} className="fill-[var(--fg-muted)]" fontSize={11}>
              {Math.round(b.budgetShare * 100)} % · {b.adsets} annonsesett · {b.ads} annonser
            </text>
            {b.id === 'motor' && (
              <text x={COLS[1]! + 14} y={y - h / 2 + 62} className="fill-[var(--fg-muted)]" fontSize={11}>
                + testbanen ved siden av
              </text>
            )}
          </g>
        );
      })}

      <Box x={COLS[2]!} y={landY.site} w={200} title="Prøvetimesiden" sub="Booker selv i Nono-widgeten" tone="booking" />
      <Box x={COLS[2]!} y={landY.form} w={200} title="Direkteskjema" sub="Vertskapet ringer" tone="lead" />
      <Box x={COLS[3]!} y={learnY.schedule} w={200} title="Schedule (CAPI)" sub="Ekte booking, person-nivå" tone="booking" />
      <Box x={COLS[3]!} y={learnY.crm} w={200} title="CRM-trinn" sub="Booket → møtte → medlem" tone="lead" />

      <text x={FW - 20} y={FH - 24} textAnchor="end" className="fill-[var(--fg-subtle)]" fontSize={11}>
        Meta finner flere som ligner på dem som faktisk booker.
      </text>
    </svg>
  );
}

function Box({ x, y, w, title, sub, tone }: { x: number; y: number; w: number; title: string; sub: string; tone: 'booking' | 'lead' }) {
  return (
    <g>
      <rect x={x} y={y - 26} width={w} height={52} rx={10} fill="var(--surface)" stroke={tone === 'booking' ? 'var(--accent-booking)' : 'var(--accent-lead)'} strokeWidth={1.5} />
      <text x={x + 12} y={y - 4} className="fill-[var(--fg)] font-medium" fontSize={13}>
        {title}
      </text>
      <text x={x + 12} y={y + 13} className="fill-[var(--fg-subtle)]" fontSize={11}>
        {sub}
      </text>
    </g>
  );
}

/* ── The account today next to the plan ─────────────────────────────────── */

function NowVsPlan({ d }: { d: MetaStructureResponse }) {
  const fx = d.fxAedToNok;
  const live = d.campaigns.filter(isLive);
  const sets = live.flatMap((c) => c.adsets.filter(isLive));
  const ads = flatAds(d).filter((f) => isLive(f.ad) && isLive(f.adset) && isLive(f.campaign));
  const budget = live.reduce((n, c) => n + (c.dailyBudget ?? sets.filter((s) => c.adsets.includes(s)).reduce((m, s) => m + (s.dailyBudget ?? 0), 0)), 0) * fx;
  const booking = sets.some((s) => resultKind(s) === 'booking');
  const warm = live.some((c) => /varm|retarget|remarket/i.test(c.name));
  const test = sets.some((s) => /test/i.test(s.name) && s.dailyBudget !== null);

  const rows: { k: string; now: string; plan: string; ok: boolean }[] = [
    { k: 'Kampanjer som går', now: String(live.length), plan: '3', ok: live.length <= 3 },
    { k: 'Annonsesett som går', now: String(sets.length), plan: '3 + testbane', ok: sets.length <= 4 },
    { k: 'Annonser som går', now: String(ads.length), plan: '8–12', ok: ads.length >= 8 && ads.length <= 12 },
    { k: 'Budsjett per dag', now: kr(budget), plan: `~${kr(DEFAULT_DAILY_NOK)}`, ok: budget >= DEFAULT_DAILY_NOK * 0.85 },
    { k: 'Optimaliserer for booking', now: booking ? 'Ja' : 'Nei', plan: 'Ja, 70 % av budsjettet', ok: booking },
    { k: 'Varme folk (retargeting)', now: warm ? 'Ja' : 'Nei', plan: 'Ja, 10 %', ok: warm },
    { k: 'Testbane med eget budsjett', now: test ? 'Ja' : 'Nei', plan: 'Ja', ok: test },
  ];

  return (
    <Card className="p-3 sm:p-4">
      <h2 className="mb-2 text-section text-fg">I dag mot systemet</h2>
      <table className="w-full text-label">
        <thead className="text-left text-fg-subtle">
          <tr>
            <th className="py-1.5 font-medium" />
            <th className="py-1.5 font-medium">Nå</th>
            <th className="py-1.5 font-medium">Systemet</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.k} className="border-t border-border">
              <td className="py-1.5 pr-2 text-fg-muted">{r.k}</td>
              <td className={cn('tabular py-1.5 pr-2 font-medium', r.ok ? 'text-good-fg' : 'text-bad-fg')}>{r.now}</td>
              <td className="py-1.5 text-fg">{r.plan}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

/* ── Budget lever ────────────────────────────────────────────────────────── */

type Econ = ReturnType<typeof unitCosts>;

function BudgetLever({ budget, setBudget, econ }: { budget: number; setBudget: (n: number) => void; econ: Econ }) {
  const [motor, skjema, varm] = BLUEPRINT;
  const cpb = econ.costPerBooking;
  const formCpb = econ.costPerLead ? econ.costPerLead / FORM_BOOK_RATE : null;
  // Warm audiences are usually cheaper; assume the motor's cost until we have our own number.
  const perWeek =
    (cpb ? ((budget * ((motor?.budgetShare ?? 0) + (varm?.budgetShare ?? 0)) * 7) / cpb) : 0) +
    (formCpb ? (budget * (skjema?.budgetShare ?? 0) * 7) / formCpb : 0);
  const membersMonth = perWeek * (30 / 7) * SHOW_UP * JOIN;

  return (
    <Card className="p-3 sm:p-4">
      <h2 className="text-section text-fg">Hva budsjettet gir</h2>
      <p className="mt-0.5 text-label text-fg-muted">
        Regnet med kontoens egne tall: {kr(cpb)} per booking på nettsiden, {kr(econ.costPerLead)} per skjemalead der{' '}
        {Math.round(FORM_BOOK_RATE * 100)} % booker ({kr(formCpb)} per booking).
      </p>
      <label className="mt-3 flex items-center gap-3">
        <span className="text-label text-fg-subtle">Per dag</span>
        <input
          type="range"
          min={200}
          max={2000}
          step={20}
          value={budget}
          onChange={(e) => setBudget(Number(e.target.value))}
          className="flex-1 accent-[var(--brand)]"
        />
        <span className="tabular w-20 text-right text-body font-semibold text-fg">{kr(budget)}</span>
      </label>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {BLUEPRINT.map((b) => (
          <div key={b.id} className="rounded-md bg-surface-2 p-2">
            <div className="truncate text-micro text-fg-subtle">{b.name}</div>
            <div className="tabular text-body font-medium text-fg">{kr(budget * b.budgetShare)}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-md border border-border p-2.5">
          <div className="text-micro text-fg-subtle">Bookinger per uke</div>
          <div className="tabular text-title text-fg">≈ {perWeek.toFixed(1).replace('.', ',')}</div>
        </div>
        <div className="rounded-md border border-border p-2.5">
          <div className="text-micro text-fg-subtle">Nye medlemmer per måned</div>
          <div className="tabular text-title text-fg">≈ {int(Math.round(membersMonth))}</div>
        </div>
      </div>
      <p className="mt-2 text-micro text-fg-subtle">
        Antar {Math.round(SHOW_UP * 100)} % oppmøte og {Math.round(JOIN * 100)} % som blir medlem (bookinger i egen widget).
        Målet i handlingsplanen er minst 26 nye medlemmer i måneden. Høyere budsjett i Moss gir høyere frekvens, så prisen per booking stiger.
      </p>
    </Card>
  );
}
